function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import { noop, __DEV__ } from "@chakra-ui/utils";
import * as React from "react";
import useColorModeState from "./use-color-mode-state";
export var ColorModeContext = /*#__PURE__*/React.createContext({
  colorMode: "light",
  toggleColorMode: noop
});

if (__DEV__) {
  ColorModeContext.displayName = "ColorModeContext";
}
/**
 * React hook that reads from `ColorModeProvider` context
 * Returns the color mode and function to toggle it
 */


export function useColorMode() {
  return React.useContext(ColorModeContext);
}

/**
 * Provides context for the color mode based on config in `theme`
 * Returns the color mode and function to toggle the color mode
 */
export function ColorModeProvider(props) {
  var {
    value,
    children,
    useSystemColorMode = false,
    defaultValue = "light"
  } = props;
  var config = {
    useSystemColorMode,
    initialColorMode: defaultValue
  };
  var [colorMode, setColorMode] = useColorModeState(config);

  var toggleColorMode = () => setColorMode(colorMode === "light" ? "dark" : "light");

  var context = {
    colorMode,
    toggleColorMode
  };
  var controlledContext = {
    colorMode: value,
    toggleColorMode: noop
  };
  return /*#__PURE__*/React.createElement(ColorModeContext.Provider, {
    value: value ? controlledContext : context
  }, children);
}

if (__DEV__) {
  ColorModeProvider.displayName = "ColorModeProvider";
}
/**
 * Locks the color mode to `dark`, without any way to change it.
 */


export var DarkMode = props => /*#__PURE__*/React.createElement(ColorModeContext.Provider, _extends({
  value: {
    colorMode: "dark",
    toggleColorMode: noop
  }
}, props));

if (__DEV__) {
  DarkMode.displayName = "DarkMode";
}
/**
 * Locks the color mode to `light` without any way to change it.
 */


export var LightMode = props => /*#__PURE__*/React.createElement(ColorModeContext.Provider, _extends({
  value: {
    colorMode: "light",
    toggleColorMode: noop
  }
}, props));

if (__DEV__) {
  LightMode.displayName = "LightMode";
}
/**
 * Change value based on color mode
 *
 * @param light the light mode value
 * @param dark the dark mode value
 */


export function getColorModeValue(light, dark) {
  return colorMode => colorMode === "light" ? light : dark;
}
/**
 * Change value based on color mode.
 *
 * @param light the light mode value
 * @param dark the dark mode value
 *
 * @example
 *
 * ```js
 * const Icon = useColorModeValue(MoonIcon, SunIcon)
 * ```
 */

export function useColorModeValue(light, dark) {
  var {
    colorMode
  } = useColorMode();
  return getColorModeValue(light, dark)(colorMode);
}
//# sourceMappingURL=color-mode.js.map